import React, { useEffect, useState } from "react";
import {
  isAndroid,
  isChrome,
  isFirefox,
  isIOS,
  isSafari,
  osVersion,
} from "react-device-detect";
import { useHistory } from "react-router-dom";
import { Textfit } from "react-textfit";

import { UnsupportedDialogue } from "./UnsupportedDialogue";
import * as constants from "../constants";
import home_background from "../images/barnes-v2-landing.png";
import barnes_logo from "../images/Barnes_logo.svg";
import close_icon from "../images/cross.svg";
import kf_logo from "../images/knight-foundation-logo.svg";

import { logiPhoneModel } from "../helpers/googleAnalyticsHelpers";
import { track } from "../helpers/analytics";
import { useLocalStorage } from "../hooks/useLocalStorage";
import withTranslation, { WithTranslationState } from "./withTranslation";
import { warm as warmCollectionCache } from "../helpers/collectionCache";
import {
  acquireCameraStream,
  releaseCameraStream,
} from "../helpers/cameraStream";
import { useHomeTemplate } from "../hooks/useHomeTemplate";

// FOC-203: where a tenant home template's {{scan_url}} / {{away_url}} links point
// (kept in step with SCAN_PATH / AWAY_HASH in server/page-render/renderPage.ts).
const SCAN_LINK = /^\s*\/scan\/?\s*$/;
const AWAY_LINK = "#focus-away";

export const HomeComponent: React.FC<WithTranslationState> = ({
  getTranslation,
  selectedLanguage,
}) => {
  const { resetLocalStorage } = useLocalStorage();
  const history = useHistory();
  const [userAtBarnes, setUserAtBarnes] = useState(null);
  const [unsupportedIOSVersion, setUnsupportedIOSVersion] = useState(null);
  const [unsupportedIOSBrowser, setUnsupportedIOSBrowser] = useState(null);
  const [cameraAccessible, setCameraAccessible] = useState(null);
  const [showError, setShowError] = useState(false);
  // FOC-203: the tenant's own home screen, if it has one (null → built-in screen).
  const { ready: templateReady, home } = useHomeTemplate(
    selectedLanguage?.code
  );

  // Styles
  let homeContainerStyle =
    unsupportedIOSBrowser || unsupportedIOSVersion
      ? { filter: "blur(10px)", transform: "scale(1.2)" }
      : {};
  let visitOnlineLinkStyle =
    localStorage.getItem(constants.SNAP_LANGUAGE_PREFERENCE) === "Ja"
      ? { fontSize: `18px` }
      : {};

  useEffect(() => {
    resetLocalStorage();
    track("home_view");
  }, []);

  useEffect(() => {
    if (isIOS) {
      checkForGetUserMedia();
    }
  }, []);

  // Prefetch the permanent-collection snapshot while the visitor is reading
  // the "Are you at the Barnes?" prompt. By the time they tap Yes and reach
  // /scan, lookups are in memory and a successful match navigates to the
  // result page synchronously. Fire-and-forget — errors fall through to the
  // per-artwork API on lookup.
  useEffect(() => {
    warmCollectionCache().catch(() => {
      /* non-fatal: lookups will fall back to /api/artwork/:id */
    });
  }, []);

  // Fresh-start boundary: arriving at Home means a new visit ("done scanning"),
  // so release any camera stream left warm from a previous scan session. The
  // scan flow re-warms it in onSelectYes below. This runs on ENTRY (not on
  // unmount), so the stream onSelectYes warms survives the Home -> /scan
  // transition and /scan can reuse it. See helpers/cameraStream.
  useEffect(() => {
    releaseCameraStream();
  }, []);

  /** Determines if navigator.mediaDevices.getUserMedia() is available on the current iOS device */
  const checkForGetUserMedia = () => {
    // navigator.mediaDevices.getUserMedia() is only supported on iOS > 11.0 and only on Safari (not Chrome, Firefox, etc.)
    // at some point in 2022 not sure the version it became available for other browsers
    const supportedIOS = navigator.mediaDevices.getUserMedia();
    if (supportedIOS === undefined) {
      setUnsupportedIOSVersion(true);
      setShowError(true);
    }
    if (!isSafari && !isChrome && !isFirefox) {
      setUnsupportedIOSBrowser(true);
      setShowError(true);
    }
  };

  const onSelectYes = async (entry: string = "button") => {
    track("scan_start", { entry });
    // If this was an iOS device, log the model to Google Analytics
    if (isIOS) {
      logiPhoneModel();
    }

    try {
      // Warm the SHARED camera stream (helpers/cameraStream). This both
      // confirms camera permission/availability AND hands the already-open
      // stream to /scan, which reuses it instead of re-acquiring — so there is
      // no cold-acquire pause or blurred-background flash on entering scan, and
      // never two streams open at once (the FOC-123 contention that produced a
      // black/dark viewfinder). Acquired at the scanner's real constraints
      // (1920² / >=24fps) so this check reflects what /scan actually needs and
      // the warmed stream is full-resolution for the reticle crop.
      await acquireCameraStream();
      track("camera_permission", { status: "granted" });

      // TODO - Log the permission grant time if it took more than 900 ms
      // const startTime = Date.now();
      // shouldLogPermissionGrantTime(startTime);

      // Navigate to the scan page
      history.push({ pathname: "/scan" });
    } catch (error) {
      track("camera_permission", {
        status:
          (error as DOMException)?.name === "NotAllowedError"
            ? "denied"
            : "error",
        error_name: (error as DOMException)?.name,
      });
      setCameraAccessible(false);
      setShowError(true);
    }
  };

  /** Tenant template links behave exactly like today's buttons: a scan link warms
   *  the camera and checks the browser (onSelectYes), an away link shows "visit soon". */
  const onTemplateClick = (event: React.MouseEvent) => {
    const link = (event.target as Element).closest("a");
    if (!link) return;
    const href = link.getAttribute("href") || "";
    if (SCAN_LINK.test(href)) {
      event.preventDefault();
      onSelectYes("template_link");
    } else if (href === AWAY_LINK) {
      event.preventDefault();
      track("home_away", { entry: "template_link" });
      setUserAtBarnes(false);
    }
  };

  const closeCameraErrorScreen = () => {
    // The user has seen that camera is not accessible, so essentially close the screen and reset camera access and them being at the Barnes
    setCameraAccessible(null);
    setUserAtBarnes(null);
    setShowError(false);
  };

  return (
    <div className="home-wrapper" id="home-wrapper" style={homeContainerStyle}>
      {/* Show the unsupported browser dialog if the browser is not supported */}
      {unsupportedIOSBrowser ? (
        <UnsupportedDialogue
          unsupportedIOSBrowser={true}
          selectedLanguage={selectedLanguage}
        />
      ) : null}

      {/* Show the unsupported iOS version dialog if the iOS version is not supported */}
      {unsupportedIOSVersion ? (
        <UnsupportedDialogue
          unsupportedIOSVersion={true}
          selectedLanguage={selectedLanguage}
        />
      ) : null}

      <img
        src={(home && home.landingImage) || home_background}
        alt="home_background"
        style={{ width: screen.width, height: screen.height }}
      />

      {/* FOC-203: the tenant's welcome screen (server-rendered, sanitized). */}
      {home && userAtBarnes == null && !showError && (
        <div
          className="landing-screen templated-home"
          onClick={onTemplateClick}
          dangerouslySetInnerHTML={{ __html: home.welcome }}
        />
      )}

      {/* Only show the initial Welcome Screen prompt if they haven't selected any value for userAtBarnes */}
      {!home && templateReady && userAtBarnes == null && !showError && (
        <div className="landing-screen">
          {/* logo */}
          <img
            src={barnes_logo}
            alt="barnes_logo"
            className="logo-center"
            role="img"
            aria-label="Welcome to the Barnes Focus Digital Guide"
          />
          <div className="user-loc-prompt">
            {getTranslation("Welcome_screen", "text_1")}{" "}
            <br aria-hidden={true} />
            {getTranslation("Welcome_screen", "text_2")}
          </div>
          <div className="home-action">
            <button className="action-btn" onClick={() => onSelectYes()}>
              <span className="action-text h2">
                <Textfit mode="single" max={25}>
                  {getTranslation("Welcome_screen", "text_3")}
                </Textfit>
              </span>
            </button>
            <button
              className="action-btn"
              onClick={() => {
                track("home_away", { entry: "button" });
                setUserAtBarnes(false);
              }}
            >
              <span className="action-text h2">
                <Textfit mode="single" max={25}>
                  {getTranslation("Welcome_screen", "text_4")}
                </Textfit>
              </span>
            </button>
          </div>
          <div className="kf-banner">
            <img
              src={kf_logo}
              alt="knight_foundation_logo"
              className="kf-logo"
            />
            <div className="kf-text caption">
              {getTranslation("About", "text_2")}
            </div>
          </div>
        </div>
      )}

      {/* FOC-203: the tenant's "visit soon" screen, when its template has one. */}
      {home && home.away.trim() !== "" && userAtBarnes === false && (
        <div>
          <div
            className="app-usage-alert h2 templated-away"
            onClick={onTemplateClick}
            dangerouslySetInnerHTML={{ __html: home.away }}
          />
          <div className="btn-close" onClick={() => setUserAtBarnes(null)}>
            <img src={close_icon} alt="close" />
          </div>
        </div>
      )}

      {/* If the user has selected that they're not at the Barnes, show this section */}
      {!(home && home.away.trim() !== "") && userAtBarnes === false && (
        <div>
          <div className="app-usage-alert h2">
            <div className="app-usage-msg">
              <span>{getTranslation("Visit_soon", "text_1")}</span>
              <span> {getTranslation("Visit_soon", "text_2")}</span>
            </div>
            <div className="visit-online-link" style={visitOnlineLinkStyle}>
              <a
                href="https://www.barnesfoundation.org/"
                target="_blank"
                rel="noreferrer"
              >
                {getTranslation("Visit_soon", "text_3")}
              </a>
            </div>
          </div>
          <div className="btn-close" onClick={() => setUserAtBarnes(null)}>
            <img src={close_icon} alt="close" />
          </div>
        </div>
      )}

      {/* If the camera is not accessible */}
      {cameraAccessible === false && (
        <div>
          <div className="app-usage-alert h2">
            <div className="app-usage-msg">
              {isIOS && <span>{constants.GET_USER_MEDIA_ERROR_IOS}</span>}
              {isAndroid && (
                <span>{constants.GET_USER_MEDIA_ERROR_ANDROID}</span>
              )}
            </div>
          </div>
          <div className="btn-close" onClick={closeCameraErrorScreen}>
            <img src={close_icon} alt="close" />
          </div>
        </div>
      )}
    </div>
  );
};

export const Home = withTranslation(HomeComponent);
